import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { TelegramLogoIcon, SpinnerGapIcon, PlusIcon, TrashIcon, ArrowSquareOutIcon, WarningIcon } from "@phosphor-icons/react";
import { fetchTgChannels, addTgChannel, removeTgChannel, fetchTgSignals } from "../lib/api";
import { useSeo } from "../lib/useSeo";

const BLUE = "#29A9EB";
const ago = (ts) => {
  if (!ts) return "";
  const s = Math.floor(Date.now() / 1000 - ts);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function SignalScanner() {
  const qc = useQueryClient();
  const [handle, setHandle] = useState("");
  const [busy, setBusy] = useState(false);

  useSeo({
    title: "🔔 Signal Scanner — Watch Public Telegram Alpha Channels | AlphaApeing",
    description: "Monitor the public Telegram alpha channels you pick and get flagged the moment a coin gets called — tagged with the exact channel it came from.",
  });

  const { data: chData, isLoading } = useQuery({ queryKey: ["tgChannels"], queryFn: fetchTgChannels, refetchInterval: 30000 });
  const { data: sigData } = useQuery({ queryKey: ["tgSignals"], queryFn: fetchTgSignals, refetchInterval: 15000 });
  const enabled = chData?.enabled;
  const channels = chData?.channels || [];
  const signals = sigData?.signals || [];

  const add = async () => {
    if (!handle.trim()) return;
    setBusy(true);
    try {
      const res = await addTgChannel(handle.trim());
      toast.success(`Now watching ${res.handle}`);
      setHandle("");
      qc.invalidateQueries({ queryKey: ["tgChannels"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add that channel");
    } finally {
      setBusy(false);
    }
  };
  const remove = async (h) => {
    await removeTgChannel(h);
    qc.invalidateQueries({ queryKey: ["tgChannels"] });
    toast.success(`Stopped watching ${h}`);
  };

  return (
    <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up" data-testid="signal-scanner-page">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center text-white" style={{ background: BLUE }}><TelegramLogoIcon size={24} weight="fill" /></div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">SIGNAL <span style={{ color: BLUE }}>SCANNER</span></h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm leading-relaxed max-w-2xl">
          Watch the <span className="text-white font-semibold">public Telegram alpha channels</span> you pick. The moment a coin gets called, it lands here —
          <span style={{ color: BLUE }} className="font-semibold"> tagged with the exact channel</span> — and pings you on Telegram. Turn "Called on Telegram" on in <Link to="/alerts" className="text-neon hover:underline">Alerts</Link>.
        </p>
      </div>

      {!isLoading && !enabled && (
        <div className="border p-4 flex items-start gap-3" style={{ borderColor: "#F5A62366", background: "#F5A62310" }} data-testid="scanner-not-configured">
          <WarningIcon size={20} style={{ color: "#F5A623" }} className="shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <span className="text-white font-semibold">Scanner not connected yet.</span> It switches on once your Telegram API keys + session are added server-side. Until then you can't add channels.
          </div>
        </div>
      )}

      <div className="border border-white/10 bg-card p-4 space-y-3">
        <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Add a public channel by @handle</label>
        <div className="flex gap-2">
          <input
            data-testid="scanner-add-input"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="@alphachannel"
            disabled={!enabled}
            className="flex-1 bg-black/40 border border-white/15 px-3 py-2 text-sm font-mono text-white outline-none focus:border-neon/60 disabled:opacity-50"
          />
          <button data-testid="scanner-add-btn" onClick={add} disabled={busy || !enabled || !handle.trim()} className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-white disabled:opacity-50" style={{ background: BLUE }}>
            {busy ? <SpinnerGapIcon size={16} className="animate-spin" /> : <PlusIcon size={16} weight="bold" />} Add
          </button>
        </div>
        {channels.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1" data-testid="scanner-channels">
            {channels.map((c) => (
              <span key={c.handle} className="flex items-center gap-1.5 px-2 py-1 border border-white/12 bg-black/20 font-mono text-xs">
                {c.handle}
                <button onClick={() => remove(c.handle)} title="Stop watching" data-testid={`scanner-remove-${c.handle}`} className="text-muted-foreground hover:text-danger"><TrashIcon size={12} /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <h2 className="font-heading font-black text-xl tracking-tight flex items-center gap-2"><TelegramLogoIcon size={18} weight="fill" style={{ color: BLUE }} /> Recent calls <span className="font-mono text-xs text-muted-foreground">{signals.length}</span></h2>
        {signals.length === 0 ? (
          <div className="border border-white/10 bg-card py-14 text-center text-muted-foreground font-mono text-sm px-6">
            No calls captured yet. Add some channels above — calls (with a token, ticker or chart link) will show here in real time.
          </div>
        ) : (
          <div className="border border-white/5 divide-y divide-white/5" data-testid="scanner-signals">
            {signals.map((s) => (
              <div key={`${s.chatId}:${s.messageId}`} data-testid={`scanner-signal-${s.messageId}`} className="px-4 py-3">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-mono text-[11px] px-1.5 py-0.5 font-bold" style={{ background: `${BLUE}18`, border: `1px solid ${BLUE}`, color: BLUE }}>{s.handle || s.title || "channel"}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">{ago(s.ts)}</span>
                  {s.link && <a href={s.link} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-neon"><ArrowSquareOutIcon size={12} /></a>}
                </div>
                <div className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{s.text}</div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(s.tokens?.mints || []).map((m) => (
                    <Link key={m} to={`/coin/${m}`} className="font-mono text-[10px] px-1.5 py-0.5 border border-neon/40 text-neon hover:bg-neon/10">{m.slice(0, 4)}…{m.slice(-4)}</Link>
                  ))}
                  {(s.tokens?.tickers || []).map((t) => (
                    <span key={t} className="font-mono text-[10px] px-1.5 py-0.5 border border-white/12 text-white">${t}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
